import { useEffect } from "react";
import { FaUserFriends, FaExpandArrowsAlt, FaCheckCircle, FaPhoneAlt, FaWhatsapp, FaChair } from "react-icons/fa";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import BookingForm from "../components/BookingForm";
import CTASection from "../components/CTASection";
import { banquetData, seatingStyles, hallOccasions } from "../data/banquetData";
import { siteConfig } from "../data/siteConfig";
import "../styles/Banquet.css";

const Banquet = () => {
  useEffect(() => { document.title = "Banquet Halls | USHA Resort, Patna"; }, []);

  return (
    <>
      <PageHeader
        title="Banquet Halls"
        subtitle="Home · Banquet Hall"
        image="/images/Banquet/Hall-1.jpeg"
      />

      {/* ---------- Intro ---------- */}
      <section>
        <div className="container">
          <SectionTitle
            tag="Indoor Venues"
            title="Elegant Halls for Every Celebration"
            subtitle="Air-conditioned, beautifully designed banquet halls at USHA Resort — equipped with modern facilities for weddings, receptions, corporate events and parties."
          />

          <div className="banquet-intro gap-top">
            <div className="banquet-intro-media">
              <img src="/images/Banquet/H-main.jpg" alt="USHA Resort Banquet Hall" loading="lazy" />
            </div>
            <div className="banquet-intro-content">
              <h3>Events Handled End-to-End</h3>
              <p>
                From décor and stage setup to catering and sound — our dedicated event team
                manages everything, so your celebration goes smoothly from start to finish.
              </p>
              <ul>
                {[
                  "Multiple AC halls for every guest count",
                  "In-house catering & décor packages",
                  "Dedicated event coordinator",
                  "Flexible seating arrangements",
                  "Ample parking & 24×7 security",
                ].map((point) => (
                  <li key={point}><FaCheckCircle /> {point}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Hall cards ---------- */}
      <section className="amenities-section">
        <div className="container">
          <SectionTitle
            tag="Our Halls"
            title="Banquet Halls We Offer"
            subtitle="Choose the perfect hall based on your guest count and occasion."
          />

          <div className="banquet-grid gap-top">
            {banquetData.map((hall) => (
              <article className="hall-card" key={hall.id}>
                <div className="hall-img">
                  <img src={hall.image} alt={hall.name} loading="lazy" />
                  <span className="hall-capacity"><FaUserFriends /> {hall.capacity}</span>
                </div>

                <div className="hall-body">
                  <h3>{hall.name}</h3>

                  <div className="hall-meta">
                    <span><FaUserFriends /> {hall.capacity}</span>
                    <span><FaExpandArrowsAlt /> {hall.area}</span>
                  </div>

                  <p>{hall.short}</p>

                  <div className="hall-features">
                    {hall.features.map((f) => <span key={f}>{f}</span>)}
                  </div>

                  <div className="hall-actions">
                    <a href="#booking" className="btn btn-primary">Enquire Now</a>
                    <a
                      href={`https://wa.me/${siteConfig.whatsapp}?text=${encodeURIComponent(`Hello USHA Resort! I want to enquire about ${hall.name}.`)}`}
                      target="_blank" rel="noreferrer" className="hall-wa"
                    >
                      <FaWhatsapp /> WhatsApp
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Seating styles ---------- */}
      <section>
        <div className="container">
          <SectionTitle
            tag="Flexibility"
            title="Seating Arrangements"
            subtitle="Every event is different — we arrange seating exactly the way you need it."
          />
          <div className="seating-grid gap-top">
            {seatingStyles.map((s) => (
              <div className="seating-card" key={s.style}>
                <FaChair />
                <h4>{s.style}</h4>
                <p><FaUserFriends /> {s.capacity}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Occasions ---------- */}
      <section className="amenities-section">
        <div className="container">
          <SectionTitle
            tag="We Host"
            title="Occasions in Our Halls"
            subtitle="From sacred rituals to corporate gatherings — every event finds its perfect setting."
          />
          <div className="hall-occasions gap-top">
            {hallOccasions.map(({ icon: Icon, title }) => (
              <div className="hall-occasion" key={title}>
                <Icon />
                <span>{title}</span>
              </div>
            ))}
          </div>

          <div className="banquet-cta-note">
            <p>Need help choosing the right hall? <FaPhoneAlt /> Call us at{" "}
              <a href={siteConfig.phoneHref}>{siteConfig.phone}</a> — our team will guide you.
            </p>
          </div>
        </div>
      </section>

      <CTASection />

      {/* ---------- Booking ---------- */}
      <section className="booking-section" id="booking">
        <div className="container">
          <SectionTitle
            tag="Hall Booking"
            title="Book a Banquet Hall"
            subtitle="Share your event date and guest count — our team will confirm availability on WhatsApp."
          />
          <BookingForm />
        </div>
      </section>
    </>
  );
};

export default Banquet;